import { useState } from "react";
import axios from "axios";
import { Reveal, SectionHeading, Overline } from "./Primitives";
import { toast } from "@/components/ui/sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CONTACT, SERVICE_DETAIL_OPTIONS, COMMERCIAL_PRICING, LOGISTICS_FACTORS, ADDONS } from "@/data/content";
import { Loader2, Send, Phone, Mail, CheckCircle2, CalendarIcon, ChevronDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  { value: "residential", label: "Residential Cleaning" },
  { value: "commercial", label: "Commercial Cleaning" },
  { value: "logistics", label: "On-Demand Logistics" },
];

const initial = {
  name: "", email: "", phone: "", service_type: "residential",
  service_detail: "", address: "", preferred_date: "", message: "",
};

const inputCls =
  "bg-[#0a0a0a] border-white/15 text-white placeholder:text-zinc-600 focus-visible:ring-gold focus-visible:border-gold h-12";

const fmtDate = (d) =>
  d ? d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" }) : "";

export const QuoteForm = () => {
  const [form, setForm] = useState(initial);
  const [dateObj, setDateObj] = useState(undefined);
  const [addons, setAddons] = useState([]);
  const [showAddons, setShowAddons] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const toggleAddon = (name) =>
    setAddons((a) => (a.includes(name) ? a.filter((x) => x !== name) : [...a, name]));

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const groups = SERVICE_DETAIL_OPTIONS[form.service_type] || [];

  const changeType = (v) =>
    setForm((f) => ({ ...f, service_type: v, service_detail: "" }));

  const pickDate = (d) => {
    setDateObj(d);
    setForm((f) => ({ ...f, preferred_date: fmtDate(d) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email and phone.");
      return;
    }
    setLoading(true);
    try {
      const addonNote = addons.length ? `Requested add-ons: ${addons.join(", ")}.` : "";
      const payload = { ...form, message: [addonNote, form.message].filter(Boolean).join("\n") };
      await axios.post(`${API}/quotes`, payload);
      // Analytics events — fire ONLY after a successful quote submission.
      if (typeof window.gtag === "function") {
        // GA4 property (Measurement ID G-B3MM2F23R9)
        window.gtag("event", "ads_conversion_Request_quote_1", { send_to: "G-B3MM2F23R9" });
        // Google Ads conversion (AW-18388644850)
        window.gtag("event", "conversion", { send_to: "AW-18388644850/HI51CO-dw4IdEPLnscBE" });
      }
      setDone(true);
      toast.success("Quote request sent! Check your email for confirmation.");
      setForm(initial);
      setDateObj(undefined);
      setAddons([]);
      setShowAddons(false);
    } catch (err) {
      toast.error("Something went wrong. Please call us or try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="quote" className="bg-[#050505] py-20 md:py-32">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid gap-14 lg:grid-cols-2">
        <Reveal>
          <SectionHeading
            overline="Free Estimate"
            title={<>Request your <span className="gold-gradient italic">free quote</span></>}
            subtitle="Tell us what you need. We'll review the details and reach out with a personalized estimate. Online requests are available 24/7."
          />

          <div className="mt-10 space-y-5">
            <a href={CONTACT.phoneHref} data-testid="quote-phone-link"
              className="group flex items-center gap-4 border border-white/10 bg-[#121212] p-5 hover:border-gold transition-colors duration-300">
              <span className="w-11 h-11 flex items-center justify-center border border-gold/40 text-gold group-hover:bg-gold group-hover:text-black transition-colors">
                <Phone size={18} />
              </span>
              <div>
                <span className="block text-[10px] uppercase tracking-[0.18em] text-zinc-500">Call or text</span>
                <span className="font-display text-xl text-white">{CONTACT.phone}</span>
              </div>
            </a>
            <a href={CONTACT.emailHref} data-testid="quote-email-link"
              className="group flex items-center gap-4 border border-white/10 bg-[#121212] p-5 hover:border-gold transition-colors duration-300">
              <span className="w-11 h-11 flex items-center justify-center border border-gold/40 text-gold group-hover:bg-gold group-hover:text-black transition-colors">
                <Mail size={18} />
              </span>
              <div>
                <span className="block text-[10px] uppercase tracking-[0.18em] text-zinc-500">Email us</span>
                <span className="font-display text-lg text-white break-all">{CONTACT.email}</span>
              </div>
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="border border-white/10 bg-[#0d0d0d] p-6 md:p-9">
            {done ? (
              <div data-testid="quote-success" className="flex flex-col items-center text-center py-16">
                <CheckCircle2 size={54} className="text-gold" />
                <h3 className="mt-6 font-display text-3xl text-white">Request received</h3>
                <p className="mt-3 text-zinc-400 max-w-sm">
                  Thank you! A confirmation has been sent to your email. We'll be in touch shortly with
                  your personalized estimate.
                </p>
                <button data-testid="quote-reset-btn" onClick={() => setDone(false)}
                  className="mt-8 border border-gold text-gold px-6 py-3 hover:bg-gold/10 transition-colors">
                  Send another request
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="quote-form">
                <Overline>Quote Request</Overline>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="q-name" className="text-zinc-300 text-xs">Full Name *</Label>
                    <Input id="q-name" data-testid="quote-name" className={`mt-2 ${inputCls}`}
                      value={form.name} onChange={set("name")} placeholder="Jane Doe" />
                  </div>
                  <div>
                    <Label htmlFor="q-phone" className="text-zinc-300 text-xs">Phone *</Label>
                    <Input id="q-phone" data-testid="quote-phone" className={`mt-2 ${inputCls}`}
                      value={form.phone} onChange={set("phone")} placeholder="(929) 000-0000" />
                  </div>
                </div>

                <div>
                  <Label htmlFor="q-email" className="text-zinc-300 text-xs">Email *</Label>
                  <Input id="q-email" type="email" data-testid="quote-email" className={`mt-2 ${inputCls}`}
                    value={form.email} onChange={set("email")} placeholder="you@email.com" />
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-300 text-xs">Service Type *</Label>
                    <Select value={form.service_type} onValueChange={changeType}>
                      <SelectTrigger data-testid="quote-service-type" className={`mt-2 ${inputCls}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-white/15 text-white">
                        {SERVICE_OPTIONS.map((o) => (
                          <SelectItem key={o.value} value={o.value} data-testid={`service-opt-${o.value}`}
                            className="focus:bg-gold focus:text-black">
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-zinc-300 text-xs">Preferred Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button type="button" data-testid="quote-date-trigger"
                          className={`mt-2 w-full flex items-center justify-between px-3 ${inputCls} ${form.preferred_date ? "text-white" : "text-zinc-600"}`}>
                          {form.preferred_date || "Pick a date"}
                          <CalendarIcon size={16} className="text-gold" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent align="start" className="w-auto p-0 bg-[#121212] border-white/15">
                        <Calendar mode="single" selected={dateObj} onSelect={pickDate}
                          disabled={{ before: new Date() }} initialFocus
                          className="text-white [&_.rdp-day_selected]:bg-gold [&_.rdp-day_selected]:text-black" />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                <div>
                  <Label className="text-zinc-300 text-xs">Service Detail</Label>
                  <Select value={form.service_detail}
                    onValueChange={(v) => setForm((f) => ({ ...f, service_detail: v }))}>
                    <SelectTrigger data-testid="quote-detail" className={`mt-2 ${inputCls}`}>
                      <SelectValue placeholder="Select the option that fits your request" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-white/15 text-white max-h-72">
                      {groups.map((g) => (
                        <SelectGroup key={g.group}>
                          <SelectLabel className="text-gold text-[11px] uppercase tracking-[0.16em]">{g.group}</SelectLabel>
                          {g.items.map((it) => {
                            const val = `${g.group} — ${it}`;
                            return (
                              <SelectItem key={val} value={val} className="focus:bg-gold focus:text-black pl-4">
                                {it}
                              </SelectItem>
                            );
                          })}
                        </SelectGroup>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {form.service_type === "commercial" && (
                  <div data-testid="commercial-pricing-ref" className="border border-gold/25 bg-[#0a0a0a] p-4">
                    <span className="text-[10px] uppercase tracking-[0.18em] text-gold">Pricing guidance (customized)</span>
                    <div className="mt-3 space-y-1.5">
                      {COMMERCIAL_PRICING.map((r) => (
                        <div key={r.type} className="flex items-center justify-between text-xs">
                          <span className="text-zinc-300">{r.type}</span>
                          <span className="font-mono text-gold">{r.rate}</span>
                        </div>
                      ))}
                    </div>
                    <p className="mt-3 text-[11px] text-zinc-600">Final commercial pricing is individually reviewed per property.</p>
                  </div>
                )}

                {form.service_type === "logistics" && (
                  <div data-testid="logistics-pricing-ref" className="border border-gold/25 bg-[#0a0a0a] p-4">
                    <span className="text-[10px] uppercase tracking-[0.18em] text-gold">Pricing customized according to</span>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {LOGISTICS_FACTORS.map((f) => (
                        <span key={f} className="text-[11px] text-zinc-300 border border-white/10 px-2 py-1 font-mono">{f}</span>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <Label htmlFor="q-address" className="text-zinc-300 text-xs">Address / Neighborhood</Label>
                  <Input id="q-address" data-testid="quote-address" className={`mt-2 ${inputCls}`}
                    value={form.address} onChange={set("address")} placeholder="e.g. Astoria, Queens" />
                </div>

                {form.service_type === "residential" && (
                  <div className="border border-white/10 bg-[#0a0a0a]" data-testid="addons-panel">
                    <button
                      type="button"
                      data-testid="addons-toggle"
                      onClick={() => setShowAddons((v) => !v)}
                      className="w-full flex items-center justify-between px-4 py-3 text-left"
                    >
                      <span className="text-sm text-zinc-300">
                        Add-on services <span className="text-zinc-600">(optional)</span>
                        {addons.length > 0 && (
                          <span className="ml-2 text-gold text-xs">{addons.length} selected</span>
                        )}
                      </span>
                      <ChevronDown size={18} className={`text-gold transition-transform duration-300 ${showAddons ? "rotate-180" : ""}`} />
                    </button>
                    {showAddons && (
                      <div className="px-4 pb-4">
                        <p className="text-[11px] text-zinc-600 mb-3">Tap any add-ons to include. Prices shown are starting prices.</p>
                        <div className="flex flex-wrap gap-2">
                          {ADDONS.map((a) => {
                            const sel = addons.includes(a.name);
                            return (
                              <button
                                key={a.name}
                                type="button"
                                data-testid={`addon-chip-${a.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                onClick={() => toggleAddon(a.name)}
                                className={`text-xs px-3 py-1.5 border transition-colors duration-200 ${
                                  sel ? "bg-gold text-black border-gold" : "border-white/15 text-zinc-300 hover:border-gold/50"
                                }`}
                              >
                                {a.name} · {a.price}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <Label htmlFor="q-message" className="text-zinc-300 text-xs">Anything else?</Label>
                  <Textarea id="q-message" data-testid="quote-message" rows={3}
                    className="mt-2 bg-[#0a0a0a] border-white/15 text-white placeholder:text-zinc-600 focus-visible:ring-gold focus-visible:border-gold"
                    value={form.message} onChange={set("message")}
                    placeholder="Frequency (weekly/biweekly/monthly), stops, item details, add-ons, condition, etc." />
                </div>

                <button type="submit" data-testid="quote-submit" disabled={loading}
                  className="w-full bg-gold text-black font-semibold py-4 hover:bg-[#c5a059] transition-colors duration-300 flex items-center justify-center gap-2 disabled:opacity-60">
                  {loading ? <><Loader2 size={18} className="animate-spin" /> Sending…</>
                    : <><Send size={18} /> Send My Quote Request</>}
                </button>
                <p className="text-[11px] text-zinc-600 text-center">
                  Free, no-obligation estimate. We'll email you a confirmation right away.
                </p>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
